import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "react-hot-toast";

// Context & Components
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import Navbar from "./components/Navbar";
import PublicRoute from "./components/PublicRoute";//Public routes that protect the login user to see the public routes 
// Layouts
import AdminLayout from "./layouts/AdminLayout";
import UserLayout from "./layouts/UserLayout";

// Public Pages
import Home from "./pages/public/Home";
import Competitions from "./pages/public/Competitions";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import HowItWorks from "./pages/public/HowItWorks";
import About from "./pages/public/About";
import Contact from "./pages/public/Contact";
import Fqa from "./pages/public/Fqa";
import VerifyCertificate from "./pages/public/VerifyCertificate";
import NotFound from "./pages/public/NotFound";

// User Pages
import Dashboard from "./pages/user/Dashboard";
import TeamDashboard from "./pages/user/TeamDashboard";
import Certificate from "./pages/user/Certificates";
import MyCompetitions from "./pages/user/MyCompetitions";
import InternshipRoom from "./pages/user/InternshipRoom";
import Internships from "./pages/user/Internships";
import StageOne from "./pages/user/StageOne";
import StageTwo from "./pages/user/StageTwo";
import CompetitionHub from "./pages/user/CompetitionHub";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminPayments from "./pages/admin/Payments";
import AdminSettings from "./pages/admin/Settings";
import AdminCompetitions from "./pages/admin/AdminCompetitions";
import AdminInterns from "./pages/admin/AdminInterns";
import AdminCertificates from "./pages/admin/AdminCertificate";
import LiveMonitor from "./pages/admin/LiveMonitor";
import QuizManager from "./pages/admin/QuizManager";
import AssignmentManager from "./pages/admin/AssignmentManager";
import InternshipAssignmentManager from "./pages/admin/InternshipAssignmentManager";
import InternshipTracker from "./pages/admin/InternshipTracker";


//Protect form inspect

import useDevToolsProtection from "./useDevToolsProtection";


function App() {
  useDevToolsProtection();//Custom hook to prevent dev tools access in production
  
  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <div className="flex flex-col min-h-screen overflow-x-hidden relative">
            <Navbar />
            <Routes>
              <Route
                path="/verify-certificate/:serial"
                element={<VerifyCertificate />}
              />
              {/* --- GUEST ONLY ZONE --- */}
              {/* If a logged-in user hits any of these, they get kicked to /dashboard */}
              <Route element={<PublicRoute />}>
                <Route path="/" element={<Home />} />
                <Route path="/competitions" element={<Competitions />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/fqa" element={<Fqa />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
              </Route>

              {/* --- USER DASHBOARD (NESTED) --- */}
              {/* We open the Route tag here to allow children inside */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <UserLayout />
                  </ProtectedRoute>
                }
              >
                {/* These routes render inside the UserLayout's <Outlet /> */}
                <Route index element={<Dashboard />} />
                <Route path="team" element={<TeamDashboard />} />
                <Route path="my-competitions" element={<MyCompetitions />} />
                <Route path="certificates" element={<Certificate />} />
                <Route path="competitions" element={<Competitions />} />
                <Route
                  path="competition-hub/:competitionId"
                  element={<CompetitionHub />}
                />
                <Route path="my-internships" element={<Internships />} />
                <Route path="faq" element={<Fqa />} />
                <Route path="contact" element={<Contact />} />
                <Route
                  path="internship-room/:id"
                  element={<InternshipRoom />}
                />
              </Route>

              {/* --- THE ARENA ZONE (FULL SCREEN) --- */}
              {/* By putting this outside the Navbar and Layout, it gets the whole screen */}
              <Route
                path="/arena/:competitionId"
                element={
                  <ProtectedRoute>
                    <StageOne />
                  </ProtectedRoute>
                }
              />

              {/* --- THE FOCUS ZONE (STAGE TWO - FULL SCREEN) --- */}
              <Route
                path="/mission-stage-two/:competitionId"
                element={
                  <ProtectedRoute>
                    <StageTwo />
                  </ProtectedRoute>
                }
              />

              {/* --- ADMIN ROUTES (NESTED) --- */}
              <Route
                path="/admin"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout />
                  </ProtectedAdminRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="live-monitor" element={<LiveMonitor />} />
                <Route path="quiz-manager" element={<QuizManager />} />
                <Route
                  path="assignment-manager"
                  element={<AssignmentManager />}
                />
                <Route
                  path="internship-weekly-assignment-manager"
                  element={<InternshipAssignmentManager />}
                />
                <Route
                  path="internship-tracker"
                  element={<InternshipTracker />}
                />
                <Route path="users" element={<AdminUsers />} />
                <Route path="competitions" element={<AdminCompetitions />} />
                <Route path="certificates" element={<AdminCertificates />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="interns" element={<AdminInterns />} />
              </Route>

              {/* NOT FOUND PAGE*/}
              <Route path="*" element={<NotFound />} />
            </Routes>

            <Toaster position="top-center" reverseOrder={false} />
          </div>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
